import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { styled } from '@mui/material/styles'
import type { TCountryFeelgreat } from '@/core/store/country.store'
import { CountryItem } from './country-item.component'

type ContinentGroup = { continent: string; list: TCountryFeelgreat[] }

interface CollapseSelectCountryProps {
    data: ContinentGroup[]
    value: number | null
    setValue: (v: number | null) => void
    handleClickChangeCountry: (country: TCountryFeelgreat, languageCode: string) => void
    translate: (word: string) => string
}

const ChevronIcon = ({ expanded }: { expanded: boolean }) => (
    <svg
        width="12"
        height="8"
        viewBox="0 0 12 8"
        fill="none"
        style={{
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.15s'
        }}
        data-testid="collapse-select-country-svg">
        <path d="M1.41 0.59L6 5.17L10.59 0.59L12 2L6 8L0 2L1.41 0.59Z" fill="#153862" data-testid="collapse-select-country-path" />
    </svg>
)

export const CollapseSelectCountry = ({ data, value, setValue, handleClickChangeCountry, translate }: CollapseSelectCountryProps) => {
    return (
        <Stack sx={{ width: '100%', mt: '30px', mb: '40px' }} data-testid="collapse-select-country-stack">
            {data.map((item, itemKey) => (
                <AccordionStyled
                    key={itemKey}
                    expanded={value === itemKey}
                    disableGutters
                    onChange={() => setValue(value === itemKey ? null : itemKey)}
                    data-testid="collapse-select-country-accordion-styled">
                    <AccordionSummaryStyled data-testid="collapse-select-country-accordion-summary-styled">
                        <Box display="flex" alignItems="center" data-testid="collapse-select-country-box-typography">
                            <Typography
                                sx={{ fontSize: '16px', fontFamily: "'Inter', 'Noto Sans Thai', sans-serif" }}
                                data-testid="collapse-select-country-typography">
                                {translate(item.continent)}
                            </Typography>
                        </Box>
                        <ChevronIcon expanded={value === itemKey} data-testid="collapse-select-country-chevron-icon" />
                    </AccordionSummaryStyled>
                    <AccordionDetails sx={{ p: 0 }} data-testid="collapse-select-country-accordion-details">
                        <CountryListGrid data-testid="collapse-select-country-country-list-grid">
                            {item.list.map((country, index) => {
                                if (shouldHideCountry(country)) return null
                                return (
                                    <CountryItem
                                        country={country}
                                        key={`country-${index}`}
                                        handleClick={handleClickChangeCountry}
                                        data-testid="collapse-select-country-country-item"
                                    />
                                )
                            })}
                        </CountryListGrid>
                    </AccordionDetails>
                </AccordionStyled>
            ))}
        </Stack>
    )
}

function shouldHideCountry(country: TCountryFeelgreat): boolean {
    const mm = country.maintenance_mode
    if (!mm?.maintenance_mode) return false
    const text = mm as unknown as { text?: { english?: string; native?: string } }
    if (/developing/.test(text.text?.english ?? '') || /developing/.test(text.text?.native ?? '')) {
        if (/fg\.unicity\.com/.test(window.location.hostname)) return true
    }
    return false
}

const AccordionStyled = styled(Accordion)`
    background-color: transparent;
    box-shadow: none;
    margin-bottom: 40px;
    &::before {
        background-color: transparent;
    }
    &.Mui-expanded {
        margin: 0 0 40px;
    }
`

const AccordionSummaryStyled = styled(AccordionSummary)`
    &.MuiAccordionSummary-root {
        padding: 0;
        min-height: auto;
        .MuiAccordionSummary-content {
            gap: 10px;
            margin: 0;
        }
    }
`

const CountryListGrid = styled(Box)`
    display: grid;
    margin-top: 20px;
    border-top: 1px solid #d1d9e2;
    border-bottom: 1px solid #d1d9e2;
    padding: 20px 0;
    grid-template-columns: 1fr;
    gap: 20px;

    @media (min-width: 600px) {
        grid-template-columns: 1fr 1fr;
    }
`
